/**
 * ReceivedNoteMap
 * A quiet, non-interactive map backdrop for the received-note reveal.
 * Starts zoomed out over the note's location, then — when `fly` flips true —
 * glides down to street level and drops a pin, so the note feels "pinned to a
 * place." Self-contained (no app chrome, no other notes, no auth gate).
 *
 * If the style/tiles fail to load, it calls onError so the page can fall back
 * to the plain warm background instead of a blank void.
 */

import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

const STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';
const START = { zoom: 2.6, pitch: 0 };
const ARRIVE = { zoom: 16.5, pitch: 58, duration: 2600 };
const LOAD_TIMEOUT_MS = 8000;

export default function ReceivedNoteMap({ lat, lng, fly, reduceMotion, onArrived, onError }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const flownRef = useRef(false);
  const arrivedCbRef = useRef(onArrived);
  const errorCbRef = useRef(onError);
  arrivedCbRef.current = onArrived;
  errorCbRef.current = onError;

  // Create the map once.
  useEffect(() => {
    const nLat = parseFloat(lat);
    const nLng = parseFloat(lng);
    if (!containerRef.current || mapRef.current) return;
    if (!isFinite(nLat) || !isFinite(nLng)) {
      errorCbRef.current?.();
      return;
    }

    let map;
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style: STYLE_URL,
        center: [nLng, nLat],
        zoom: START.zoom,
        pitch: START.pitch,
        interactive: false, // cinematic backdrop, not a toy
        attributionControl: false,
        maxTileCacheSize: 40,
      });
    } catch (e) {
      errorCbRef.current?.();
      return;
    }
    mapRef.current = map;

    // Tile fetch failures shouldn't crash the reveal — swallow them.
    map.on('error', () => {});

    // If the style never loads (offline / blocked), fall back gracefully.
    const timeout = setTimeout(() => {
      if (!map.isStyleLoaded()) errorCbRef.current?.();
    }, LOAD_TIMEOUT_MS);

    map.once('load', () => {
      clearTimeout(timeout);
      const el = document.createElement('div');
      el.className = 'rn-map-pin';
      new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([nLng, nLat])
        .addTo(map);
    });

    return () => {
      clearTimeout(timeout);
      map.remove();
      mapRef.current = null;
    };
  }, [lat, lng]);

  // Fly in once, when asked.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !fly || flownRef.current) return;
    flownRef.current = true;

    const nLat = parseFloat(lat);
    const nLng = parseFloat(lng);

    const arrive = () => {
      if (reduceMotion) {
        map.jumpTo({ center: [nLng, nLat], zoom: ARRIVE.zoom, pitch: ARRIVE.pitch });
        arrivedCbRef.current?.();
        return;
      }
      map.flyTo({
        center: [nLng, nLat],
        zoom: ARRIVE.zoom,
        pitch: ARRIVE.pitch,
        bearing: 0,
        duration: ARRIVE.duration,
        essential: true,
      });
      map.once('moveend', () => arrivedCbRef.current?.());
    };

    if (map.isStyleLoaded()) arrive();
    else map.once('load', arrive);
  }, [fly, lat, lng, reduceMotion]);

  return <div ref={containerRef} className="rn-map" aria-hidden="true" />;
}
